/**
 * Iter144 — Full-page Document Templates workspace.
 *
 * Replaces the old TemplatesModal popup. Reusable retainer / disclosure /
 * ATA cover documents can be saved once, previewed, edited, and sent to
 * any client with a single click.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  ArrowLeft, FileText, Plus, Save, Zap, Trash2, Edit3, RefreshCw, X,
} from 'lucide-react';

const KINDS = [
  { value: 'general', label: 'General' },
  { value: 'retainer', label: 'Retainer' },
  { value: 'disclosure', label: 'Disclosure' },
  { value: 'ata_cover', label: 'ATA Cover' },
];

const EMPTY = { name: '', title: '', description: '', body: '', kind: 'general', sections: [] };

export default function ClientDocumentTemplates() {
  const navigate = useNavigate();
  const token = localStorage.getItem('nexus_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [applyingClientId, setApplyingClientId] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, cRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/client-documents/templates`, { headers }),
        fetch(`${BACKEND_URL}/api/clients`, { headers }),
      ]);
      if (tRes.ok) setTemplates(await tRes.json());
      if (cRes.ok) setClients(await cRes.json());
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const active = templates.find(t => t.id === activeId) || null;

  const startEdit = (tpl) => {
    setEditing({ ...EMPTY, ...tpl, sections: tpl?.sections || [] });
    setActiveId(tpl?.id || null);
    setApplyingClientId('');
  };
  const cancelEdit = () => { setEditing(null); };

  const save = async () => {
    if (!editing?.name?.trim() || !editing?.title?.trim()) {
      toast({ title: 'Template name and title are required.', variant: 'destructive' }); return;
    }
    const method = editing.id ? 'PATCH' : 'POST';
    const url = editing.id
      ? `${BACKEND_URL}/api/client-documents/templates/${editing.id}`
      : `${BACKEND_URL}/api/client-documents/templates`;
    const r = await fetch(url, {
      method, headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(editing),
    });
    if (!r.ok) { const d = await r.json().catch(() => ({})); return toast({ title: 'Save failed', description: d?.detail || '', variant: 'destructive' }); }
    const saved = await r.json();
    toast({ title: 'Template saved' });
    setEditing(null);
    await load();
    setActiveId(saved.id || editing.id);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    const r = await fetch(`${BACKEND_URL}/api/client-documents/templates/${id}`, { method: 'DELETE', headers });
    if (r.ok) { toast({ title: 'Template deleted' }); if (activeId === id) setActiveId(null); load(); }
  };

  const applyToClient = async () => {
    if (!applyingClientId) return toast({ title: 'Pick a client first', variant: 'destructive' });
    if (!active) return;
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/client-documents/from-template`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: active.id, client_id: applyingClientId }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); return toast({ title: 'Failed', description: d?.detail || '', variant: 'destructive' }); }
      toast({ title: 'Document sent', description: 'The client will be notified once the agency countersigns.' });
      setApplyingClientId('');
    } finally { setSending(false); }
  };

  const addSection = () => setEditing(e => ({ ...e, sections: [...(e.sections || []), { label: 'Signature', kind: 'signature', required: true }] }));
  const updateSection = (i, patch) => setEditing(e => ({ ...e, sections: e.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const removeSection = (i) => setEditing(e => ({ ...e, sections: e.sections.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-6" data-testid="client-doc-templates-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/client-documents')}
                  className="text-[#94A3B8] hover:text-white" data-testid="tpl-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="w-12 h-12 flex items-center justify-center rounded-lg" style={{ background: '#2563EB', boxShadow: '0 6px 14px -4px rgba(59,130,246,0.55)' }}>
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="nx-eyebrow">Admin · Documents</div>
            <h1 className="nx-h1 mt-1">Document Templates</h1>
            <p className="nx-body mt-1">Save common documents once (retainer, disclosure, ATA cover) and send them to any client with a single click.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}
                  className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]" data-testid="tpl-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={() => startEdit(EMPTY)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tpl-new-btn">
            <Plus className="w-4 h-4 mr-2" /> New template
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Left list */}
        <Card className="bg-[#131A2A] border-[#334155]">
          <CardContent className="p-3 space-y-2 max-h-[70vh] overflow-y-auto">
            {loading && <div className="text-sm text-[#94A3B8] p-2">Loading…</div>}
            {!loading && templates.length === 0 && (
              <div className="text-sm text-[#94A3B8] p-6 text-center border border-dashed border-[#334155] rounded">
                No templates yet. Press <span className="text-white font-semibold">New template</span> to create your first reusable document.
              </div>
            )}
            {templates.map(t => (
              <button key={t.id}
                      onClick={() => { setActiveId(t.id); setEditing(null); setApplyingClientId(''); }}
                      data-testid={`tpl-list-${t.id}`}
                      className={`w-full text-left rounded border p-3 transition-colors ${activeId === t.id
                        ? 'border-emerald-500/60 bg-emerald-500/10'
                        : 'border-[#334155] bg-[#0B0F19] hover:border-[#3B82F6]/40'}`}>
                <div className="text-[#F8FAFC] font-semibold truncate">{t.title}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] mt-0.5">{t.kind} · {t.name}</div>
                {t.description && <div className="text-xs text-[#CBD5E1] mt-1 line-clamp-2">{t.description}</div>}
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Right pane */}
        <div className="space-y-4">
          {editing ? (
            <Card className="bg-[#131A2A] border-[#334155]">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">{editing.id ? 'Edit template' : 'New template'}</h2>
                  <Button variant="ghost" onClick={cancelEdit} className="text-[#94A3B8]"><X className="w-4 h-4" /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Internal name *</Label>
                    <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}
                           className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="tpl-name" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Kind</Label>
                    <Select value={editing.kind} onValueChange={v => setEditing({ ...editing, kind: v })}>
                      <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="tpl-kind"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#131A2A] border-[#334155]">
                        {KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Document title *</Label>
                  <Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })}
                         className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="tpl-title" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Description</Label>
                  <Textarea rows={2} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })}
                            className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Body</Label>
                  <Textarea rows={10} value={editing.body} onChange={e => setEditing({ ...editing, body: e.target.value })}
                            placeholder="Full document body — appears on the signing page and in the exported PDF."
                            className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC] font-mono text-sm" data-testid="tpl-body" />
                </div>
                {/* Iter144 — Signing sections (docusign-style anchors) */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Signing sections</Label>
                    <Button size="sm" variant="outline" onClick={addSection}
                            className="border-[#334155] text-[#CBD5E1] h-7" data-testid="tpl-add-section">
                      <Plus className="w-3 h-3 mr-1" /> Add section
                    </Button>
                  </div>
                  <p className="text-[11px] text-[#94A3B8] mt-1">Each section is a labeled zone the signer must complete (signature, initial, date, or free-text).</p>
                  <div className="mt-2 space-y-2">
                    {(editing.sections || []).length === 0 && (
                      <div className="text-xs text-[#94A3B8] p-3 border border-dashed border-[#334155] rounded text-center">
                        No sections yet. Add one and the client signing page will prompt for each in order.
                      </div>
                    )}
                    {(editing.sections || []).map((s, i) => (
                      <div key={i} className="rounded border border-[#334155] bg-[#0B0F19] p-2 flex items-center gap-2" data-testid={`tpl-section-${i}`}>
                        <span className="text-xs text-[#94A3B8] font-mono w-6">#{i + 1}</span>
                        <Input value={s.label} onChange={e => updateSection(i, { label: e.target.value })}
                               className="bg-[#131A2A] border-[#334155] text-[#F8FAFC] h-8 flex-1" placeholder="Label" />
                        <Select value={s.kind} onValueChange={v => updateSection(i, { kind: v })}>
                          <SelectTrigger className="bg-[#131A2A] border-[#334155] text-[#F8FAFC] h-8 w-32"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#131A2A] border-[#334155]">
                            <SelectItem value="signature">Signature</SelectItem>
                            <SelectItem value="initial">Initial</SelectItem>
                            <SelectItem value="date">Date</SelectItem>
                            <SelectItem value="text">Free text</SelectItem>
                            <SelectItem value="checkbox">Checkbox</SelectItem>
                          </SelectContent>
                        </Select>
                        <label className="text-[11px] text-[#CBD5E1] flex items-center gap-1">
                          <input type="checkbox" checked={!!s.required} onChange={e => updateSection(i, { required: e.target.checked })} />
                          Required
                        </label>
                        <Button size="sm" variant="ghost" onClick={() => removeSection(i)}
                                className="text-red-400 h-7 px-2" data-testid={`tpl-section-remove-${i}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={cancelEdit}
                          className="border-[#334155] text-[#94A3B8]">Cancel</Button>
                  <Button onClick={save}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tpl-save-btn">
                    <Save className="w-4 h-4 mr-2" /> {editing.id ? 'Save changes' : 'Create template'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : active ? (
            <>
              <Card className="bg-[#131A2A] border-[#334155]">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{active.kind} · {active.name}</div>
                      <h2 className="text-xl font-semibold text-white mt-0.5">{active.title}</h2>
                      {active.description && <p className="text-sm text-[#CBD5E1] mt-1">{active.description}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => startEdit(active)}
                              className="border-[#334155] text-[#CBD5E1]" data-testid="tpl-edit-btn">
                        <Edit3 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button variant="ghost" onClick={() => del(active.id)}
                              className="text-red-400" data-testid="tpl-delete-btn">
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-[#94A3B8] tracking-wider mb-1">Preview</div>
                    <pre className="bg-[#0B0F19] border border-[#334155] rounded p-3 text-sm text-[#CBD5E1] font-mono whitespace-pre-wrap max-h-[320px] overflow-y-auto">{active.body || '— empty —'}</pre>
                  </div>
                  {active.sections && active.sections.length > 0 && (
                    <div>
                      <div className="text-xs uppercase text-[#94A3B8] tracking-wider mb-1">Signing sections ({active.sections.length})</div>
                      <div className="flex flex-wrap gap-1.5">
                        {active.sections.map((s, i) => (
                          <span key={i} className="text-[11px] px-2 py-0.5 rounded border border-[#334155] bg-[#0B0F19] text-[#CBD5E1]">
                            #{i + 1} {s.label} · <span className="text-[#94A3B8]">{s.kind}</span>{s.required ? ' *' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Send to client */}
              <Card className="bg-[#131A2A] border-[#334155]">
                <CardContent className="p-5 space-y-3">
                  <h3 className="text-sm font-semibold text-white uppercase tracking-wide">Send to client</h3>
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                    <div>
                      <Label className="text-xs uppercase text-[#94A3B8] tracking-wider">Client</Label>
                      <Select value={applyingClientId} onValueChange={setApplyingClientId}>
                        <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="tpl-apply-client">
                          <SelectValue placeholder="Choose a client" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#131A2A] border-[#334155] max-h-[260px]">
                          {clients.map(c => (
                            <SelectItem key={c.id} value={c.id} className="text-[#F8FAFC]">
                              {c.name || c.full_name || c.id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={applyToClient} disabled={!applyingClientId || sending}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tpl-apply-btn">
                      <Zap className="w-4 h-4 mr-2" /> {sending ? 'Sending…' : 'Send now'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-[#131A2A] border-[#334155]">
              <CardContent className="p-10 text-center text-[#94A3B8]">
                Select a template on the left to preview, edit, or send — or press <span className="text-white font-semibold">New template</span> above.
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
